import { atom } from "jotai";
import { COMMUNES_OPTIONS } from "./communes";
import { ORGANISMES_FINANCEMENT_DATAS } from "./organismesFinancement";

export const stepsConfig = [
  {
    id: "vehicule",
    title: "Véhicule",
    showTitle: true,
    shortTitle: "Véhicule",
    icon: "fa-car",
  },
  {
    id: "conducteur",
    title: "Conducteur",
    showTitle: true,
    shortTitle: "Conducteur",
    icon: "fa-id-card",
  },
  {
    id: "formule",
    title: "Formule",
    showTitle: true,
    shortTitle: "Formule",
    icon: "fa-cog",
  },
  {
    id: "garanties",
    title: "Options",
    showTitle: true,
    shortTitle: "Options",
    icon: "fa-shield-alt",
  },
  // Étape 2 du formulaire (PER-2428) : le second questionnaire, ouvert après
  // l'estimation, qui finalise la demande. Réponses stockées dans productdata.
  {
    id: "devis",
    title: "Devis prêt à signer (étape 2)",
    showTitle: true,
    shortTitle: "Devis",
    icon: "fa-file-signature",
  },
];

const OUI_NON = [
  { name: "Oui", id: "1" },
  { name: "Non", id: "2" },
];

/** Responsabilité dans un sinistre déclaré (PER-2466) ; « partagée » compte comme responsable chez les compagnies. */
const RESPONSABILITES_SINISTRE = [
  { name: "Responsable", id: "responsable" },
  { name: "En partie responsable", id: "partagee" },
  { name: "Non responsable", id: "non_responsable" },
];

export const questionsConfigAtom = atom({
  // CATÉGORIE: UTILISATEUR
  civilite_utilisateur: {
    category: "utilisateur",
    label: "Vous êtes ?",
    type: "BTN_LIST",
    name: "civilite_utilisateur",
    datas: [
      {
        name: "Un homme",
        id: "1",
      },
      {
        name: "Une femme",
        id: "2",
      },
    ],
    defaultValue: "2",
  },
  nom_utilisateur: {
    category: "utilisateur",
    label: "Votre nom",
    type: "TEXT",
    name: "nom_utilisateur",
    placeholder: "Ex: Dupont",
    defaultValue: "Doe",
  },
  prenom_utilisateur: {
    category: "utilisateur",
    label: "Votre prénom",
    type: "TEXT",
    name: "prenom_utilisateur",
    placeholder: "Ex: Jean",
    defaultValue: "John",
  },
  telephone_utilisateur: {
    category: "utilisateur",
    label: "Votre numéro de téléphone",
    type: "TEXT",
    name: "telephone_utilisateur",
    placeholder: "Ex: 0692123456",
    defaultValue: "0693353535",
  },
  email_utilisateur: {
    category: "utilisateur",
    label: "Votre adresse email",
    type: "TEXT",
    name: "email_utilisateur",
    placeholder: "Ex: jean.dupont@email.com",
    defaultValue: "test@domaine.com",
  },

  // CATÉGORIE: VÉHICULE
  marque_vehicule: {
    category: "vehicule",
    label: "Marque du véhicule",
    type: "TEXT",
    name: "marque_vehicule",
    placeholder: "Ex: Peugeot, Renault, BMW...",
    defaultValue: "",
  },
  modele_vehicule: {
    category: "vehicule",
    label: "Modèle du véhicule",
    type: "TEXT",
    name: "modele_vehicule",
    placeholder: "Ex: 308, Clio, Série 3...",
    defaultValue: "",
  },
  version_vehicule: {
    category: "vehicule",
    label: "Version",
    type: "TEXT",
    name: "version_vehicule",
    placeholder: "Ex: GTI, Sport, Confort...",
    defaultValue: "",
  },

  immatriculation_vehicule: {
    category: "vehicule",
    label: "Numéro d'immatriculation",
    type: "TEXT",
    name: "immatriculation_vehicule",
    placeholder: "Ex: AA-123-BB ou 123-ABC-12",
    defaultValue: "",
  },

  energie_vehicule: {
    category: "vehicule",
    label: "Énergie",
    type: "SELECT",
    name: "energie_vehicule",
    datas: [
      {
        name: "Essence",
        id: "ES",
      },
      {
        name: "Diesel",
        id: "GO",
      },
      {
        name: "Électrique",
        id: "EL",
      },
      {
        name: "Hybride",
        id: "HY",
      },
      {
        name: "GPL",
        id: "GP",
      },
    ],
    defaultValue: "",
  },
  puissance_fiscale: {
    category: "vehicule",
    label: "Puissance fiscale",
    type: "NUMBER_INT",
    name: "puissance_fiscale",
    placeholder: "Ex: 7 (en chevaux fiscaux)",
    defaultValue: "",
  },
  // Segment véhicule depuis PER-2428 (exigé par les compagnies, carte grise S.1).
  nombre_places: {
    category: "vehicule",
    label: "Nombre de places",
    type: "NUMBER_INT",
    name: "nombre_places",
    placeholder: "Ex: 5 (places assises, conducteur compris)",
    defaultValue: "",
  },
  carrosserie_vehicule: {
    category: "vehicule",
    label: "Carrosserie",
    type: "SELECT",
    name: "carrosserie_vehicule",
    datas: [
      {
        name: "Citadine",
        id: "citadine",
      },
      {
        name: "Berline",
        id: "berline",
      },
      {
        name: "Break",
        id: "break",
      },
      {
        name: "Coupé",
        id: "coupe",
      },
      {
        name: "Cabriolet",
        id: "cabriolet",
      },
      {
        name: "Monospace",
        id: "monospace",
      },
      {
        name: "SUV",
        id: "suv",
      },
      {
        name: "4x4",
        id: "4x4",
      },
      {
        name: "Utilitaire",
        id: "utilitaire",
      },
    ],
    defaultValue: "",
  },

  date_acquisition: {
    category: "vehicule",
    label: "Date d'acquisition",
    type: "DATE",
    name: "date_acquisition",
    defaultValue: null,
  },
  date_premiere_circulation: {
    category: "vehicule",
    label: "Date de première mise en circulation",
    type: "DATE",
    name: "date_premiere_circulation",
    defaultValue: null,
  },
  financement_vehicule: {
    category: "vehicule",
    label: "Mode de financement du véhicule",
    type: "BTN_LIST",
    name: "financement_vehicule",
    datas: [
      {
        name: "Comptant",
        id: "comptant",
      },
      {
        name: "Crédit automobile",
        id: "credit",
      },
      {
        name: "Leasing",
        id: "leasing",
      },
      {
        name: "Location longue durée",
        id: "lld",
      },
    ],
    defaultValue: "",
  },

  lieu_garage: {
    category: "vehicule",
    label: "Lieu de garage du véhicule",
    type: "SELECT",
    name: "lieu_garage",
    datas: COMMUNES_OPTIONS,
    defaultValue: "",
  },
  type_garage: {
    category: "vehicule",
    label: "Type de garage (où le véhicule est stationné la nuit)",
    type: "BTN_LIST",
    name: "type_garage",
    datas: [
      {
        name: "Garage fermé",
        id: "ferme",
      },
      {
        name: "Parking couvert",
        id: "couvert",
      },
      {
        name: "Parking découvert",
        id: "decouvert",
      },
      {
        name: "Pas de garage clos",
        id: "aucun",
      },
    ],
    defaultValue: "",
  },

  usage_vehicule: {
    category: "vehicule",
    label: "Usage du véhicule",
    type: "BTN_LIST",
    name: "usage_vehicule",
    datas: [
      {
        name: "Privé uniquement",
        id: "prive",
      },
      {
        name: "Privé / trajet travail",
        id: "prive_travail",
      },
      {
        name: "Professionnel",
        id: "professionnel",
      },
    ],
    defaultValue: "",
  },
  kilometrage_annuel: {
    category: "vehicule",
    label: "Kilométrage annuel parcouru avec le véhicule à assurer",
    type: "NUMBER_INT",
    name: "kilometrage_annuel",
    placeholder: "Ex: 12000",
    defaultValue: "",
  },

  // CATÉGORIE: CONDUCTEUR
  commune_utilisateur: {
    category: "conducteur",
    label: "Quelle est votre commune de résidence ?",
    type: "SELECT",
    name: "commune_utilisateur",
    datas: COMMUNES_OPTIONS,
    defaultValue: "",
  },
  conducteur1_date_naissance: {
    category: "conducteur",
    label: "Date de naissance du conducteur principal",
    type: "DATE",
    name: "conducteur1_date_naissance",
    defaultValue: "",
  },
  conducteur1_date_permis: {
    category: "conducteur",
    label: "Date d'obtention du permis",
    type: "DATE",
    name: "conducteur1_date_permis",
    defaultValue: "",
  },
  conducteur1_bonus_malus: {
    category: "conducteur",
    label: "Bonus/Malus",
    type: "TEXT",
    name: "conducteur1_bonus_malus",
    placeholder: "Ex: 0.76, 1.00, 1.25",
    defaultValue: "",
  },
  // CATÉGORIE: FORMULE
  formule: {
    category: "formule",
    label: "Type de formule souhaité",
    type: "BTN_LIST",
    name: "formule",
    datas: [
      {
        name: "Essentiel (Tiers)",
        id: "essentiel",
      },

      {
        name: "Confort (Tiers étendu)",
        id: "confort",
      },
      {
        name: "Sérénité (Tous risques modulable)",
        id: "serenite",
      },
    ],
    defaultValue: "",
  },

  contrat_avec_franchise: {
    category: "formule",
    label: "Souhaitez-vous un contrat avec ou sans franchise ?",
    type: "BTN_LIST",
    name: "contrat_avec_franchise",
    datas: [
      {
        name: "Avec franchise",
        id: "1",
      },
      {
        name: "Sans franchise",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: ["confort", "serenite"],
      operator: "in",
    },
  },

  // CATÉGORIE: GARANTIES
  garantie_du_conducteur_active: {
    category: "garanties",
    label: "Garantie du conducteur",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_du_conducteur_active",
    info: "Couvre vos blessures en cas d'accident, même responsable, ou d'agression lors d'un vol.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: ["essentiel", "confort", "serenite"],
      operator: "in",
    },
  },
  garantie_accessoires_amenagements_active: {
    category: "garanties",
    label: "Accessoires et aménagements",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_accessoires_amenagements_active",
    info: "Rembourse les accessoires ou aménagements endommagés après un sinistre garanti.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: "confort",
      operator: "equals",
    },
  },
  garantie_assistance_active: {
    category: "garanties",
    label: "Assistance",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_assistance_active",
    info: "Prend en charge le remorquage, le retour à domicile et le rapatriement en cas de panne ou d'accident.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: "essentiel",
      operator: "equals",
    },
  },
  garantie_protection_juridique_active: {
    category: "garanties",
    label: "Protection juridique",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_protection_juridique_active",
    info: "Vous défend et vous conseille en cas de litige ou d'infraction liée à votre véhicule.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: ["essentiel", "confort", "serenite"],
      operator: "in",
    },
  },
  garantie_vehicule_remplacement_active: {
    category: "garanties",
    label: "Véhicule de remplacement",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_vehicule_remplacement_active",
    info: "Fournit une voiture de prêt en cas d'immobilisation après un sinistre.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: ["confort", "serenite"],
      operator: "in",
    },
  },

  garantie_indemnisation_valeur_majoree_active: {
    category: "garanties",
    label: "Indemnisation valeur majorée",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_indemnisation_valeur_majoree_active",
    info: "Verse une indemnité 30% supérieure à la valeur estimée du véhicule en cas de perte totale.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "formule",
      value: ["confort", "serenite"],
      operator: "in",
    },
  },
  garantie_remboursement_credit_active: {
    category: "garanties",
    label: "Remboursement du crédit",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_remboursement_credit_active",
    info: "Règle le solde du prêt auto si l'indemnisation est inférieure au capital restant dû.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: [
      { dependsOn: "formule", value: "serenite", operator: "equals" },
      { dependsOn: "financement_vehicule", value: "comptant", operator: "not_equals" },
      { dependsOn: "financement_vehicule", value: "lld", operator: "not_equals" },
    ],
  },
  garantie_pack_valeur_neuf_active: {
    category: "garanties",
    label: "Pack valeur à neuf",
    type: "BTN_LIST",
    isBooleanBtnList: true,
    name: "garantie_pack_valeur_neuf_active",
    info: "Rembourse le véhicule à sa valeur neuve pendant 2 ans, puis +30% de sa valeur d'expert.",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: [
      { dependsOn: "formule", value: "serenite", operator: "equals" },
      { dependsOn: "date_premiere_circulation", value: 24, operator: "within_months" },
    ],
  },

  // CATÉGORIE: DEVIS PRÊT À SIGNER (étape 2, PER-2428)
  // Mêmes clés et mêmes listes que le formulaire du site.
  adresse_utilisateur: {
    category: "devis",
    label: "Adresse (numéro et voie)",
    type: "TEXT",
    name: "adresse_utilisateur",
    placeholder: "Ex: 12 rue des Flamboyants",
    defaultValue: "",
  },
  code_postal_utilisateur: {
    category: "devis",
    label: "Code postal",
    type: "TEXT",
    name: "code_postal_utilisateur",
    placeholder: "Ex: 97400",
    defaultValue: "",
  },
  ville_utilisateur: {
    category: "devis",
    label: "Ville",
    type: "TEXT",
    name: "ville_utilisateur",
    placeholder: "Ex: Saint-Denis",
    defaultValue: "",
  },
  ville_naissance: {
    category: "devis",
    label: "Ville de naissance",
    type: "TEXT",
    name: "ville_naissance",
    placeholder: "Ex: Saint-Pierre",
    defaultValue: "",
  },
  code_postal_naissance: {
    category: "devis",
    optionnel: true,
    label: "Code postal de naissance",
    type: "TEXT",
    name: "code_postal_naissance",
    placeholder: "Ex: 97410",
    defaultValue: "",
  },
  pays_naissance: {
    category: "devis",
    label: "Pays de naissance",
    type: "TEXT",
    name: "pays_naissance",
    placeholder: "Ex: France",
    defaultValue: "",
  },
  situation_professionnelle: {
    category: "devis",
    label: "Situation professionnelle",
    type: "SELECT",
    name: "situation_professionnelle",
    datas: [
      { name: "Artisan", id: "artisan" },
      { name: "Cadre", id: "cadre" },
      { name: "Commerçant", id: "commercant" },
      { name: "Demandeur d'emploi", id: "demandeur_emploi" },
      { name: "Étudiant", id: "etudiant" },
      { name: "Fonctionnaire", id: "fonctionnaire" },
      { name: "Gérant de société", id: "gerant_societe" },
      { name: "Profession libérale", id: "liberal" },
      { name: "Militaire", id: "militaire" },
      { name: "Retraité", id: "retraite" },
      { name: "Salarié", id: "salarie" },
    ],
    defaultValue: "",
  },
  situation_familiale: {
    category: "devis",
    label: "Situation familiale",
    type: "SELECT",
    name: "situation_familiale",
    datas: [
      { name: "Célibataire", id: "celibataire" },
      { name: "Marié(e)", id: "marie" },
      { name: "Pacsé(e)", id: "pacse" },
      { name: "En concubinage", id: "concubinage" },
      { name: "Séparé(e)", id: "separe" },
      { name: "Divorcé(e)", id: "divorce" },
      { name: "Veuf / veuve", id: "veuf" },
    ],
    defaultValue: "",
  },
  enfants_a_charge: {
    category: "devis",
    optionnel: true,
    label: "Nombre d'enfants à charge",
    type: "NUMBER_INT",
    name: "enfants_a_charge",
    placeholder: "Ex: 0",
    defaultValue: "",
  },
  conduite_accompagnee: {
    category: "devis",
    label: "Conduite accompagnée",
    type: "BTN_LIST",
    name: "conduite_accompagnee",
    datas: OUI_NON,
    defaultValue: "",
  },
  suspension_permis: {
    category: "devis",
    label: "Suspension, annulation ou retrait de permis",
    type: "BTN_LIST",
    name: "suspension_permis",
    datas: OUI_NON,
    defaultValue: "",
  },
  condamnation_alcool_stupefiants: {
    category: "devis",
    label: "Condamnation alcool / stupéfiants (36 derniers mois)",
    type: "BTN_LIST",
    name: "condamnation_alcool_stupefiants",
    datas: OUI_NON,
    defaultValue: "",
  },
  accident_responsable_alcool_stupefiants: {
    category: "devis",
    label: "Accident responsable sous alcool / stupéfiants (36 derniers mois)",
    type: "BTN_LIST",
    name: "accident_responsable_alcool_stupefiants",
    datas: OUI_NON,
    defaultValue: "",
  },
  // Sinistres des 36 derniers mois (PER-2466, option B décidée le 06/10/2026) :
  // en étape 2 avec la responsabilité de chacun, comme sur le site.
  sinistres_36_mois: {
    category: "devis",
    label:
      "Avez-vous déclaré un ou plusieurs sinistres au cours des 36 derniers mois ?",
    type: "BTN_LIST",
    name: "sinistres_36_mois",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
  },
  sinistre1_nature: {
    category: "devis",
    label: "Quelle est la nature du 1er sinistre ?",
    type: "SELECT",
    name: "sinistre1_nature",
    datas: [
      {
        name: "Collision",
        id: "collision",
      },
      {
        name: "Vol / Incendie",
        id: "vol_incendie",
      },
      {
        name: "Stationnement (véhicule endommagé à l'arrêt)",
        id: "stationnement",
      },
      {
        name: "Bris de glace",
        id: "bris_glace",
      },
      {
        name: "Dégâts naturels",
        id: "degats_naturels",
      },
      {
        name: "Autre",
        id: "autre",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "sinistres_36_mois",
      value: "1",
      operator: "equals",
    },
  },
  sinistre1_date: {
    category: "devis",
    label: "Date du 1er sinistre",
    type: "DATE",
    name: "sinistre1_date",
    defaultValue: "",
    condition: {
      dependsOn: "sinistres_36_mois",
      value: "1",
      operator: "equals",
    },
  },
  sinistre1_responsabilite: {
    category: "devis",
    label: "Responsabilité dans le 1er sinistre",
    type: "BTN_LIST",
    name: "sinistre1_responsabilite",
    datas: RESPONSABILITES_SINISTRE,
    defaultValue: "",
    condition: {
      dependsOn: "sinistres_36_mois",
      value: "1",
      operator: "equals",
    },
  },
  sinistre1_tiers_identifie: {
    category: "devis",
    label: "Un tiers a-t-il été identifié pour ce sinistre ?",
    type: "BTN_LIST",
    name: "sinistre1_tiers_identifie",
    datas: OUI_NON,
    defaultValue: "",
    // Sinistre à 0 % de responsabilité seulement (décision de Perceval du 27/10/2026, PER-2496) : Allianz demande
    // « Avec tiers identifié ? » sur ces lignes et la réponse joue sur le tarif.
    condition: [
      { dependsOn: "sinistres_36_mois", value: "1", operator: "equals" },
      { dependsOn: "sinistre1_responsabilite", value: "non_responsable", operator: "equals" },
    ],
  },
  sinistre2_presence: {
    category: "devis",
    label: "Avez-vous eu un deuxième sinistre au cours des 36 derniers mois ?",
    type: "BTN_LIST",
    name: "sinistre2_presence",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "sinistres_36_mois",
      value: "1",
      operator: "equals",
    },
  },
  sinistre2_nature: {
    category: "devis",
    label: "Quelle est la nature du 2ème sinistre ?",
    type: "SELECT",
    name: "sinistre2_nature",
    datas: [
      {
        name: "Collision",
        id: "collision",
      },
      {
        name: "Vol / Incendie",
        id: "vol_incendie",
      },
      {
        name: "Stationnement (véhicule endommagé à l'arrêt)",
        id: "stationnement",
      },
      {
        name: "Bris de glace",
        id: "bris_glace",
      },
      {
        name: "Dégâts naturels",
        id: "degats_naturels",
      },
      {
        name: "Autre",
        id: "autre",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "sinistre2_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre2_date: {
    category: "devis",
    label: "Date du 2ème sinistre",
    type: "DATE",
    name: "sinistre2_date",
    defaultValue: "",
    condition: {
      dependsOn: "sinistre2_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre2_responsabilite: {
    category: "devis",
    label: "Responsabilité dans le 2ème sinistre",
    type: "BTN_LIST",
    name: "sinistre2_responsabilite",
    datas: RESPONSABILITES_SINISTRE,
    defaultValue: "",
    condition: {
      dependsOn: "sinistre2_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre2_tiers_identifie: {
    category: "devis",
    label: "Un tiers a-t-il été identifié pour ce sinistre ?",
    type: "BTN_LIST",
    name: "sinistre2_tiers_identifie",
    datas: OUI_NON,
    defaultValue: "",
    // Sinistre à 0 % de responsabilité seulement (décision de Perceval du 27/10/2026, PER-2496) : Allianz demande
    // « Avec tiers identifié ? » sur ces lignes et la réponse joue sur le tarif.
    condition: [
      { dependsOn: "sinistre2_presence", value: "1", operator: "equals" },
      { dependsOn: "sinistre2_responsabilite", value: "non_responsable", operator: "equals" },
    ],
  },
  sinistre3_presence: {
    category: "devis",
    label: "Avez-vous eu un troisième sinistre au cours des 36 derniers mois ?",
    type: "BTN_LIST",
    name: "sinistre3_presence",
    datas: [
      {
        name: "Oui",
        id: "1",
      },
      {
        name: "Non",
        id: "2",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "sinistre2_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre3_nature: {
    category: "devis",
    label: "Quelle est la nature du 3ème sinistre ?",
    type: "SELECT",
    name: "sinistre3_nature",
    datas: [
      {
        name: "Collision",
        id: "collision",
      },
      {
        name: "Vol / Incendie",
        id: "vol_incendie",
      },
      {
        name: "Stationnement (véhicule endommagé à l'arrêt)",
        id: "stationnement",
      },
      {
        name: "Bris de glace",
        id: "bris_glace",
      },
      {
        name: "Dégâts naturels",
        id: "degats_naturels",
      },
      {
        name: "Autre",
        id: "autre",
      },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "sinistre3_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre3_date: {
    category: "devis",
    label: "Date du 3ème sinistre",
    type: "DATE",
    name: "sinistre3_date",
    defaultValue: "",
    condition: {
      dependsOn: "sinistre3_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre3_responsabilite: {
    category: "devis",
    label: "Responsabilité dans le 3ème sinistre",
    type: "BTN_LIST",
    name: "sinistre3_responsabilite",
    datas: RESPONSABILITES_SINISTRE,
    defaultValue: "",
    condition: {
      dependsOn: "sinistre3_presence",
      value: "1",
      operator: "equals",
    },
  },
  sinistre3_tiers_identifie: {
    category: "devis",
    label: "Un tiers a-t-il été identifié pour ce sinistre ?",
    type: "BTN_LIST",
    name: "sinistre3_tiers_identifie",
    datas: OUI_NON,
    defaultValue: "",
    // Sinistre à 0 % de responsabilité seulement (décision de Perceval du 27/10/2026, PER-2496) : Allianz demande
    // « Avec tiers identifié ? » sur ces lignes et la réponse joue sur le tarif.
    condition: [
      { dependsOn: "sinistre3_presence", value: "1", operator: "equals" },
      { dependsOn: "sinistre3_responsabilite", value: "non_responsable", operator: "equals" },
    ],
  },
  resiliation_sinistre: {
    category: "devis",
    label: "Résiliation par un précédent assureur pour sinistre",
    type: "BTN_LIST",
    name: "resiliation_sinistre",
    datas: OUI_NON,
    defaultValue: "",
  },
  resiliation_contentieux: {
    category: "devis",
    label: "Résiliation par un précédent assureur pour non-paiement ou contentieux",
    type: "BTN_LIST",
    name: "resiliation_contentieux",
    datas: OUI_NON,
    defaultValue: "",
  },
  surendettement: {
    category: "devis",
    label: "Surendettement ou redressement judiciaire",
    type: "BTN_LIST",
    name: "surendettement",
    datas: OUI_NON,
    defaultValue: "",
  },
  vehicule_deja_assure: {
    category: "devis",
    label: "Véhicule actuellement assuré",
    type: "BTN_LIST",
    name: "vehicule_deja_assure",
    datas: OUI_NON,
    defaultValue: "",
  },
  // PER-2439 : exigences conditionnelles de la fiche projet AssurAli
  // (refus d'enregistrement sans elles, constaté le 24/09/2026).
  duree_assurance_actuelle: {
    category: "devis",
    label: "Durée d'assurance actuelle",
    type: "BTN_LIST",
    name: "duree_assurance_actuelle",
    datas: [
      { name: "Moins d'un an", id: "moins_1_an" },
      { name: "Plus d'un an", id: "plus_1_an" },
    ],
    defaultValue: "",
    condition: {
      dependsOn: "vehicule_deja_assure",
      value: "1",
      operator: "equals",
    },
  },
  organisme_financement: {
    category: "devis",
    label: "Organisme de financement (créancier gagiste)",
    // Liste des créanciers gagistes de la compagnie (organismesFinancement.js) :
    // la valeur enregistrée est le libellé exact, que l'agent retrouve sans arbitrage.
    type: "AUTOCOMPLETE",
    name: "organisme_financement",
    placeholder: "Banque, société de crédit ou de location",
    datas: ORGANISMES_FINANCEMENT_DATAS,
    defaultValue: "",
    condition: {
      dependsOn: "financement_vehicule",
      value: ["credit", "leasing", "lld"],
      operator: "in",
    },
  },
  mode_paiement: {
    category: "devis",
    label: "Mode de paiement de la cotisation",
    type: "BTN_LIST",
    name: "mode_paiement",
    datas: [
      { name: "En une fois (annuel)", id: "annuel" },
      { name: "Par prélèvement mensuel", id: "mensuel" },
    ],
    defaultValue: "",
  },
});
